<script setup>
import { computed } from 'vue';
import { CdrContainer, CdrBreadcrumb, CdrText, CdrLink } from '@rei/cedar';

const props = defineProps({
  breadcrumbs: {
    type: Object,
    required: true,
  },
  states: {
    type: Array,
    required: true,
  },
  allActivitiesTitle: {
    type: String,
    required: true,
  },
  statePrefix: {
    type: String,
    required: true,
  },
  cityPrefix: {
    type: String,
    required: true,
  },
  aaPrefix: {
    type: String,
    required: true,
  },
});

const formattedBreadcrumbs = computed(() =>
  Object.keys(props.breadcrumbs).map((key) => ({
    item: {
      url: props.breadcrumbs[key].data,
      name: props.breadcrumbs[key].name,
    },
  })),
);

const getStateUrl = (state) => `${props.statePrefix}/${state}`;
const getCityUrl = (city) => `${props.cityPrefix}/${city}`;
</script>

<template>
  <CdrContainer class="all-places">
    <CdrBreadcrumb :items="formattedBreadcrumbs" />
    <CdrText
      class="all-places__title"
      tag="h1"
    >
      All Locations
    </CdrText>
    <section class="all-places__grid">
      <div
        v-for="(state, stateIdx) in props.states"
        :key="stateIdx"
        class="all-places__state-group"
      >
        <CdrLink
          class="all-places__state-link"
          :href="getStateUrl(state.urlComponent)"
        >
          {{ state.name }}
        </CdrLink>
        <CdrLink
          v-for="(city, cityIdx) in state.cities"
          :key="cityIdx"
          class="all-places__city-link"
          :href="getCityUrl(city.urlComponent)"
        >
          {{ city.city }}
        </CdrLink>
      </div>
      <CdrLink
        class="all-places__all-activities-link"
        :href="props.aaPrefix"
      >
        {{ props.allActivitiesTitle }}
      </CdrLink>
    </section>
  </CdrContainer>
</template>

<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/scss/cdr-tokens.scss';

.all-places {
  padding-top: $cdr-space-one-x;
  padding-bottom: $cdr-space-one-x;

  &__title {
    @include cdr-text-heading-serif-800;
    margin-top: $cdr-space-one-x;
    margin-bottom: $cdr-space-three-quarter-x;
  }

  &__grid {
    column-count: 2;
    -moz-column-count: 2;
  }

  &__state-group {
    display: flex;
    flex-direction: column;
    gap: $cdr-space-half-x;
    margin-bottom: $cdr-space-one-x;
    -webkit-column-break-inside: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  &__state-link,
  &__all-activities-link,
  &__city-link {
    text-decoration: none;
    color: $cdr-color-text-primary;

    &:hover {
      text-decoration: underline;
    }
  }

  &__state-link,
  &__all-activities-link {
    @include cdr-text-utility-serif-500;
  }

  &__city-link {
    @include cdr-text-utility-sans-200;

    &:hover,
    &:focus {
      color: $cdr-color-text-primary;
    }

    &:focus {
      text-decoration: underline;
    }
  }

  @include cdr-sm-mq-up {
    &__grid {
      column-count: 3;
      -moz-column-count: 3;
    }
  }

  @include cdr-md-mq-up {
    &__grid {
      column-count: 4;
      -moz-column-count: 4;
    }
  }
}
</style>
